import React, { type ComponentPropsWithoutRef, forwardRef, type ReactNode } from 'react';
import { Blue500, Green600, Pink400, Purple500 } from '@zep-us/zus/tokens';
import { TextLongLeftIcon } from '@zep-us/zus-icons';
import { useTranslation } from 'next-i18next';
import { logger } from '@zep/core-logger';
import { CheckSmallCircleFillIcon, MessageLinesFillIcon, PlusSmallIcon } from '@/icons';
import { LocalizedOxTypeIcon } from '@/shared/components/LocalizedOxTypeIcon';
import { Button, type ButtonProps } from '@/ui_v3';
import { cn } from '@/utils';
import { type QuizType } from './type';
import { useQuizList } from './useQuizList';
type BuildQuizButtonsLayoutMode = 'page' | 'ai-shell';
export const BuildQuizButtons = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<'div'> & {
  layoutMode?: BuildQuizButtonsLayoutMode;
}>((props, ref) => {
  const {
    className,
    layoutMode = 'page',
    ...rest
  } = props;
  const {
    t
  } = useTranslation(['common', 'build']);
  const {
    handleCreate
  } = useQuizList();
  const handleButtonClick = (type: QuizType) => {
    logger.track('add_quizItem', {
      type,
      position: 'button'
    });
    handleCreate(type);
  };
  return <div {...rest} className={cn('flex w-full flex-col gap-xs', layoutMode === 'ai-shell' ? 'min-[1025px]:grid min-[1025px]:grid-cols-3 min-[1025px]:gap-sm' : 'tablet-sm:grid tablet-sm:grid-cols-3 tablet-sm:gap-sm', className)} ref={ref}>
      <BuildQuizItemButton onMouseDown={e => {
      e.stopPropagation();
      e.preventDefault();
    }} onClick={() => handleButtonClick('multiple')} text={t('build:build.multiple.button')} icon={<CheckSmallCircleFillIcon fill={Green600} />} />
      <BuildQuizItemButton onMouseDown={e => {
      e.stopPropagation();
      e.preventDefault();
    }} onClick={() => handleButtonClick('subjective')} text={t('build:build.subjective.button')} icon={<MessageLinesFillIcon fill={Blue500} />} />
      <BuildQuizItemButton onMouseDown={e => {
      e.stopPropagation();
      e.preventDefault();
    }} onClick={() => handleButtonClick('ox')} text={t('build:build.ox.button')} icon={<LocalizedOxTypeIcon size="md" fill={Purple500} />} />
      <BuildQuizItemButton onMouseDown={e => {
      e.stopPropagation();
      e.preventDefault();
    }} onClick={() => handleButtonClick('essay')} text={t('build:build.quizType.essayQuestion')} icon={<TextLongLeftIcon size="md" fill={Pink400} />} />
    </div>;
});
BuildQuizButtons.displayName = 'BuildQuizButtons';
type BuildQuizItemButtonProps = ButtonProps & {
  text: string;
  icon: ReactNode;
};
const BuildQuizItemButton = forwardRef<HTMLButtonElement, BuildQuizItemButtonProps>((props, ref) => {
  const {
    text,
    icon,
    onClick,
    ...rest
  } = props;
  return <Button {...rest} className={'flex border-opacity-assistive bg-default shadow-xs focus:focus-ring'} size={'lg'} variant={'contained'} intent={'neutral'} onClick={onClick} ref={ref}>
      {icon}
      <span>{text}</span>
      <PlusSmallIcon from={Green600} />
    </Button>;
});
BuildQuizItemButton.displayName = 'BuildQuizItemButton';